import { useMemo, useState } from 'react';
import { Badge } from '../components/Badge';
import { type Column, DataTable } from '../components/DataTable';
import {
  type DeployTarget,
  type Release,
  type ReleasesFilters,
  useReleases,
} from '../hooks/useAdminData';
import { navigate } from '../lib/navigation';

type ReleaseStatus = Release['status'];

function statusBadgeVariant(
  status: ReleaseStatus,
): 'success' | 'warning' | 'danger' | 'default' | 'info' {
  switch (status) {
    case 'live':
      return 'success';
    case 'rolling_out':
      return 'info';
    case 'rolled_back':
      return 'danger';
    case 'superseded':
      return 'warning';
    default:
      return 'default';
  }
}

const SURFACES: DeployTarget[] = ['backend', 'admin_panel', 'mobile_build', 'mobile_ota'];

function deployStatusDotClass(status: string) {
  switch (status) {
    case 'succeeded':
      return 'bg-success';
    case 'failed':
      return 'bg-danger';
    case 'reverted':
      return 'bg-warning';
    default:
      return 'bg-border';
  }
}

function SurfaceDots({ deployments }: { deployments: Release['deployments'] }) {
  return (
    <div className="flex gap-1 items-center" aria-label="deployment surfaces">
      {SURFACES.map((surface) => {
        const dep = deployments.find((d) => d.target === surface);
        const status = dep?.status ?? 'pending';
        return (
          <span
            key={surface}
            title={status}
            aria-label={`${surface}: ${status}`}
            className={`w-2.5 h-2.5 rounded-full inline-block ${deployStatusDotClass(status)}`}
          />
        );
      })}
    </div>
  );
}

const PAGE_SIZE = 20;

export function Releases() {
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);

  const filters = useMemo<ReleasesFilters>(() => {
    const f: ReleasesFilters = { page, limit: PAGE_SIZE };
    if (statusFilter) f.status = statusFilter;
    return f;
  }, [page, statusFilter]);

  const { data, isLoading } = useReleases(filters);

  const columns: Column<Release>[] = [
    {
      key: 'commitSha',
      header: 'Commit',
      render: (row) => (
        <span className="font-mono text-xs text-text" title={row.commitSha}>
          {row.commitSha.slice(0, 8)}
        </span>
      ),
    },
    {
      key: 'environment',
      header: 'Environment',
      sortable: true,
      render: (row) => <span className="text-text-secondary text-sm">{row.environment}</span>,
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      render: (row) => <Badge variant={statusBadgeVariant(row.status)}>{row.status}</Badge>,
    },
    {
      key: 'deployments',
      header: 'Surfaces',
      render: (row) => <SurfaceDots deployments={row.deployments} />,
    },
    {
      key: 'id',
      header: '',
      render: (row) => (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/releases/${row.id}`);
          }}
          className="px-3 py-1 text-xs font-medium rounded-lg bg-primary-muted text-primary hover:bg-primary hover:text-background transition-colors"
        >
          View
        </button>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Releases</h1>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setPage(1);
          }}
          aria-label="Filter by status"
          className="px-3 py-2 bg-surface border border-border rounded-lg text-sm text-text-secondary focus:outline-none focus:border-primary transition-colors"
        >
          <option value="">All Statuses</option>
          <option value="draft">Draft</option>
          <option value="live">Live</option>
          <option value="rolling_out">Rolling Out</option>
          <option value="rolled_back">Rolled Back</option>
          <option value="superseded">Superseded</option>
        </select>

      </div>

      <div className="bg-surface rounded-xl border border-border">
        <DataTable
          columns={columns}
          data={(data?.data ?? []) as (Release & Record<string, unknown>)[]}
          total={data?.total ?? 0}
          page={page}
          pageSize={PAGE_SIZE}
          onPageChange={setPage}
          onRowClick={(row) => navigate(`/releases/${(row as Release).id}`)}
          loading={isLoading}
        />
      </div>
    </div>
  );
}
